import { SetStateAction, useEffect, useState, useRef } from 'react';
import { NudosButton } from '../../../NudosComponents';
import { ICommentsAndObservations } from '../../../../types/global';
import { getUserInfoData } from '../../../../utils/getNudosData';
import CommentsList from '../CommentsList/CommentsList';
import BordMentions, { IBordMentionsRef } from '../../../NudosComponents/BordMentions/BordMentions';
import { IconOrder } from '../../../../assets/DesignSystem/SVGComponents';
import { ImentionableUser } from '../../../NudosComponents/BordMentions/MentionableUser/MentionableUser';
import { nudosGray300, nudosTextcolor } from '../../../../styles/styles.constants';
import { getBackofficePermissionsData } from '../../../../services/configuration.service';
import { displayErrorNotification } from '../../../../utils/displayNudosStandardNotifications';
import { IusersPermissionsData } from '../../../../types/configuration.types';
import BordOneToneIcon from '../../../NudosComponents/BordOneToneIcon/BordOneToneIcon';
import { isValidFile } from '../../../../utils/validateFiles';
import { IResponsibleUserData } from '../../../../types/responsibleUser.types';
import { IorganizationBusinessData } from 'types/bussines';
import { getOrganizationData } from 'services/bussines';
import GenericLoader from '../../../GenericLoader/GenericLoader';
import './CommentsContent.scss';

const CommentsContent = ({
  comments,
  handleSubmitComments,
  loading,
  isLoadingInitialComments,
  messageEmpty = 'Aquí verás los comentarios internos. De momento, no hay ninguno.',
  responsibleUsers = [],
  organizationId
}: {
  comments?: ICommentsAndObservations[];
  handleSubmitComments?: (comment: ICommentsAndObservations) => Promise<void>;
  loading: boolean;
  isLoadingInitialComments?: boolean;
  messageEmpty?: string;
  responsibleUsers?: IResponsibleUserData[];
  organizationId?: number;
}) => {
  const userInfo = getUserInfoData();

  const [commentLocal, setCommentLocal] = useState<ICommentsAndObservations>();
  const [backofficeUsers, setBackofficeUsers] = useState<ImentionableUser[]>([]);
  const [mentionedUsers, setMentionedUsers] = useState<ImentionableUser[]>([]);
  const [buttonLocalLoading, setButtonLocalLoading] = useState<boolean>(false);
  const [resetCount, setResetCount] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<File | undefined>(undefined);
  const [organizationInfo, setOrganizationInfo] = useState<IorganizationBusinessData | undefined>(undefined);
  const commentsContentRef = useRef<HTMLDivElement>(null);
  const bordMentionsRef = useRef<IBordMentionsRef>(null);
  const contentInputCommentRef = useRef<HTMLDivElement>(null);
  const messageLength = commentLocal?.comments?.length ?? 0;
  const userNameOrEmail = userInfo?.user?.toString() ?? userInfo?.email ?? '';
  const buttonIsLoading = buttonLocalLoading || loading;

  const canSubmitComment = messageLength > 0 || !!imageFile;

  const updateCommentValue = (value: SetStateAction<string>) => {
    if (typeof value === 'function') {
      setCommentLocal(prevCommentLocal => {
        if (!prevCommentLocal) return { comments: '', userName: '', mentions: [] };
        return {
          ...prevCommentLocal,
          comments: value(prevCommentLocal.comments),
          userName: prevCommentLocal?.user ?? ''
        };
      });
    } else {
      setCommentLocal({
        ...commentLocal,
        comments: value,
        user: userNameOrEmail,
        userId: userInfo?.uuid ? Number(userInfo?.uuid) : null,
        mentions: mentionedUsers.map(user => user.userId)
      });
    }
  };

  const handleSubmitComment = async () => {
    if (!canSubmitComment) return;
    try {
      setButtonLocalLoading(true);

      const commentToSend: ICommentsAndObservations = {
        ...commentLocal,
        comments: commentLocal?.comments || '',
        mentions: mentionedUsers.map(user => user.userId),
        commentImage: imageFile
      };
      await handleSubmitComments?.(commentToSend);

      if (imageFile && bordMentionsRef.current) {
        bordMentionsRef.current.handleRemoveImage();
      }

      setResetCount(prev => prev + 1);
      setCommentLocal({ comments: '', user: '', mentions: [] });
      setMentionedUsers([]);
    } catch (error) {
      displayErrorNotification({ customJSXMessage: <>Error enviando el comentario</> });
    } finally {
      setButtonLocalLoading(false);
    }
  };

  const getBackofficeUsers = async () => {
    try {
      const { usersPermissions } = await getBackofficePermissionsData();
      const users = usersPermissions.map((user: IusersPermissionsData) => ({
        userId: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email
      }));
      setBackofficeUsers(users);
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al obtener los usuarios del backoffice</>
      });
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (loading) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.types.includes('Files')) setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (loading) return;
    event.preventDefault();
    event.stopPropagation();
    const relatedTarget = event.relatedTarget as Node;
    if (!commentsContentRef.current?.contains(relatedTarget)) setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (loading) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event?.dataTransfer?.files?.[0];
    const isValid = isValidFile({
      file,
      formatErrorMessage: 'Solo puedes cargar imágenes en los comentarios',
      sizeErrorMessage: 'La imagen que intentas cargar pesa más de 30mb'
    });
    if (!isValid) return;
    if (bordMentionsRef.current && bordMentionsRef.current.handleFileChange) {
      bordMentionsRef.current.handleFileChange(file);
    }
  };

  const adjustBordMentionsHeight = () => {
    if (!contentInputCommentRef.current) return;
    const bordMentionsElements = contentInputCommentRef.current.querySelectorAll('.bordMentions, .bordMentionsInput');
    const height = !!mentionedUsers?.length ? '96px' : '66px';
    bordMentionsElements.forEach(element => {
      (element as HTMLElement).style.height = height;
    });
  };

  const getOrganizationInfo = async () => {
    if (!organizationId) return;
    setButtonLocalLoading(true);
    try {
      const organizationInfoData = await getOrganizationData(Number(organizationId));
      setOrganizationInfo(organizationInfoData);
    } catch (error) {
      displayErrorNotification({ customJSXMessage: <>Error al obtener la información de la organización</> });
    } finally {
      setButtonLocalLoading(false);
    }
  };

  useEffect(() => {
    getBackofficeUsers();
    getOrganizationInfo();
  }, []);

  useEffect(() => {
    adjustBordMentionsHeight();
  }, [!!mentionedUsers?.length, mentionedUsers]);

  return (
    <div
      ref={commentsContentRef}
      className="commentsContent"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="commentsContentDropOverlay">
          <BordOneToneIcon variant="uploadCloud" customWidth="18px" stroke="black" />
          <div>Suelta la imagen aquí</div>
        </div>
      )}

      {isLoadingInitialComments && (
        <div className="initialCommentsLoader">
          <GenericLoader sizeLoader={40} />
        </div>
      )}

      {!isLoadingInitialComments && !comments?.length && (
        <div className="noComments">
          <div className="contentIcon">
            <IconOrder />
          </div>
          <p className="messageEmpty">{messageEmpty}</p>
        </div>
      )}

      {!isLoadingInitialComments && !!comments?.length && <CommentsList comments={comments} />}
      <div ref={contentInputCommentRef} className="contentInputComment">
        <BordMentions
          ref={bordMentionsRef}
          resetCount={resetCount}
          placeholder="Escribe tus notas"
          userList={backofficeUsers}
          updateCommentsCallback={updateCommentValue}
          updateMentionedUsersCallback={setMentionedUsers}
          bordMentionsInputProps={{
            style: {
              color: commentLocal && !buttonIsLoading ? nudosTextcolor : nudosGray300,
              cursor: buttonIsLoading ? 'not-allowed' : 'auto',
              userSelect: buttonIsLoading ? 'none' : 'auto'
            }
          }}
          isLoading={buttonIsLoading}
          setImageFile={setImageFile}
          responsibleUsers={responsibleUsers}
          organizationInfo={organizationInfo}
        />
        <NudosButton
          buttonText="Guardar"
          handleClick={handleSubmitComment}
          customClassName="buttonSubmitComment"
          isButtonDisabled={!canSubmitComment || buttonIsLoading}
          isButtonLoading={buttonIsLoading}
        />
      </div>
    </div>
  );
};

export default CommentsContent;
